<template>
    <div>
        <div v-if="mode == 'readtable'">
            <div v-for="(item, index) in customFields" :key="index" class="mb-3 white-bkg b-1 br">
                <header
                    @click="item.openByDefault = !item.openByDefault"
                    class="flex-level jc-space bb-1 cpointer"
                >
                    <h3 class="m-0 p-3 flex-level">
                        {{ item.name ? item.name : $l10n.__('Groupe par défaut') }}
                    </h3>
                    <a class="gray-100 pointer flex-right p-3">
                        <i
                            class="icon-arrow-back dinlineb gray-60"
                            :class="item.openByDefault ? 'rotated-270' : 'rotated-90'"
                        ></i>
                    </a>
                </header>
                <table v-if="item.openByDefault" class="wp-10 table no-border with-vertical-line">
                    <tbody>
                        <tr v-for="(customField, cfIndex) in item.fields" :key="cfIndex">
                            <td>
                                <div class="flex-level">
                                    <div class="p-2">{{ customField.name }}</div>
                                    <el-tooltip
                                        v-if="customField.description.length"
                                        class="flex-right mr-2"
                                        effect="dark"
                                        popper-class="customfields-tool-tip"
                                        placement="bottom-start"
                                    >
                                        <template #content>
                                            <div v-html="customField.description"></div>
                                        </template>
                                        <i class="icon-help"></i>
                                    </el-tooltip>
                                </div>
                            </td>
                            <td class="relative customfields-value">
                                <CustomfieldHistory
                                    :cfid="customField.id"
                                    :cfname="customField.name"
                                    :emitEditSuccess="emitEditSuccess"
                                    :class="{ 'history-top-pos': hasHistoryTopPos(customField) }"
                                />

                                <component
                                    :key="`${customField.id}::${customFieldValues.some(
                                        ({ customFieldId }) => customFieldId === customField.id,
                                    )}`"
                                    :notStoredValue="
                                        !customFieldValues.some(
                                            ({ customFieldId }) => customFieldId === customField.id,
                                        )
                                    "
                                    :is="'cf-' + customField.type"
                                    mode="read"
                                    :prop="customField"
                                    :placeholder="customField.defaultValue"
                                    :value="correspondingData(customField)"
                                    :context="context"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div v-else>
            <el-form
                ref="customfieldForm"
                label-position="top"
                :inline-message="true"
                label-width="120px"
            >
                <div v-for="(customFieldGroup, index) in customFields" :key="index">
                    <h4>
                        <a
                            @click="
                                customFieldGroup.openByDefault = !customFieldGroup.openByDefault
                            "
                            class="gray-100 pointer"
                        >
                            {{
                                customFieldGroup.name
                                    ? customFieldGroup.name
                                    : $l10n.__('Groupe par défaut')
                            }}
                            <i
                                class="icon-arrow-back dinlineb gray-60 fright"
                                :class="
                                    customFieldGroup.openByDefault ? 'rotated-270' : 'rotated-90'
                                "
                            ></i>
                        </a>
                    </h4>

                    <div v-show="customFieldGroup.openByDefault">
                        <el-form-item
                            v-for="(customField, index) in customFieldGroup.fields"
                            :key="index"
                            :id="customField.id"
                            :prop="
                                referenceCf({
                                    customFieldId: customField.id,
                                    type: customField.type,
                                })
                            "
                            :error="
                                errors.get(
                                    referenceCf({
                                        customFieldId: customField.id,
                                        type: customField.type,
                                    }),
                                )
                            "
                        >
                            <template #label class="flex-level">
                                <div class="flex-level">
                                    <el-tooltip
                                        v-if="customField.description.length"
                                        class="mr-2"
                                        effect="dark"
                                        popper-class="w-4"
                                        placement="bottom"
                                    >
                                        <template #content>
                                            <div v-html="customField.description"></div>
                                        </template>
                                        <i class="icon-help"></i>
                                    </el-tooltip>
                                    <span>
                                        {{ labelCf(customField) }}
                                    </span>
                                </div>
                            </template>
                            <component
                                :is="'cf-' + customField.type"
                                :mode="mode"
                                :prop="customField"
                                :placeholder="customField.defaultValue"
                                :value="correspondingData(customField)"
                                :context="context"
                                @input="updateValue"
                                @remove="removeValue"
                                @error="errorValue"
                            />
                        </el-form-item>
                    </div>
                </div>
            </el-form>
        </div>
    </div>
</template>

<script>
import CfFormMixins from '../../mixins/CfFormMixins';
import CfSimpleText from 'vcomponents/inputs/custom/SimpleText.vue';
import CfRichText from 'vcomponents/inputs/custom/RichText.vue';
import CfNumeric from 'vcomponents/inputs/custom/Numeric.vue';
import CfAmount from 'vcomponents/inputs/custom/Amount.vue';
import CfUnit from 'vcomponents/inputs/custom/Unit.vue';
import CfEmail from 'vcomponents/inputs/custom/Email.vue';
import CfUrl from 'vcomponents/inputs/custom/Url.vue';
import CfDate from 'vcomponents/inputs/custom/Date.vue';
import CfCheckbox from 'vcomponents/inputs/custom/Checkbox.vue';
import CfSelect from 'vcomponents/inputs/custom/Select.vue';
import CfRadio from 'vcomponents/inputs/custom/Radio.vue';
import CfTime from 'vcomponents/inputs/custom/Time.vue';
import CfThird from 'vcomponents/inputs/custom/Third.vue';
import CfPeople from 'vcomponents/inputs/custom/People.vue';
import CfItem from 'vcomponents/inputs/custom/Item.vue';
import CfStaff from 'vcomponents/inputs/custom/Staff.vue';
import CfBoolean from 'vcomponents/inputs/custom/Boolean.vue';
import CustomfieldHistory from 'vcomponents/common/customFields/CustomfieldHistory.vue';

export default {
    components: {
        'cf-simpletext': CfSimpleText,
        'cf-richtext': CfRichText,
        'cf-numeric': CfNumeric,
        'cf-amount': CfAmount,
        'cf-unit': CfUnit,
        'cf-email': CfEmail,
        'cf-url': CfUrl,
        'cf-date': CfDate,
        'cf-checkbox': CfCheckbox,
        'cf-select': CfSelect,
        'cf-radio': CfRadio,
        'cf-time': CfTime,
        'cf-third': CfThird,
        'cf-people': CfPeople,
        'cf-item': CfItem,
        'cf-staff': CfStaff,
        'cf-boolean': CfBoolean,
        CustomfieldHistory,
    },
    mixins: [CfFormMixins],
    props: {
        cfs: {
            type: Array,
            required: true,
        },
        cfv: {
            type: Array,
            required: true,
        },
        emitEditSuccess: {
            type: String,
            required: false,
            default: 'form:customfields:edit:success',
        },
        mode: {
            type: String,
            required: false,
            default: 'edit',
        },
        errors: {
            type: Object,
            required: false,
            default: () => {},
        },
        context: {
            type: String,
            required: false,
            default: 'create',
        },
    },
    created() {
        this.customFields = this.cfs;
        this.customFieldValues = this.cfv;
        EventBus.on('listing:refresh', this.update);
        EventBus.on('form:customfields:edit:success', this.update);
    },
    destroyed() {
        EventBus.off('listing:refresh', this.update);
        EventBus.off('form:customfields:edit:success', this.update);
    },
    methods: {
        update(payload) {
            this.customFieldValues = payload.customFieldValues;
            this.customFields = payload.customFields;
        },
        hasHistoryTopPos(customfield) {
            if (['simpletext', 'richtext'].indexOf(customfield.type) === -1) {
                return false;
            }

            const value = this.correspondingData(customfield);
            if (!value) {
                return false;
            }

            return (
                (value.stringValue && value.stringValue.length > 300) ||
                (value.textValue && value.textValue.length > 300)
            );
        },
    },
};
</script>

<style>
.customfields-tool-tip {
    transform: translateX(-8.5%);
}

.customfields-value .customfields-history {
    position: absolute;
    display: flex;
    align-items: center;
    right: var(--spacing-1);
    top: 0;
    bottom: 0;
    opacity: 0;
    transition: all 0.2s linear;
    padding: var(--spacing-0);
    z-index: 2;

    &.history-top-pos {
        align-items: start;
        padding: var(--spacing-2) var(--spacing-0);
    }
}

.customfields-value:hover .customfields-history {
    opacity: 1;
}

/* Override from HoveredActions */
.customfields-value .hovered-actions {
    right: 90px !important;
}

.customfields-value:hover {
    .hovered-actions {
        opacity: 1;
    }
    .avatar-name-hovered {
        padding-right: 70px;
    }
}
</style>
